import { InvalidUrlError } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Maybe, nothing } from 'maybeasy';
import { AppyError } from '../../../../../../../Appy';
import { Error } from '../../../../../../../ErrorHandling';
import { MissingLinkError } from '../../../../../../../LinkyLinky';
import { ImportedParticipantsResource, SuccessfulParticipant } from '../../../../Store/Types';
import { ExtendedGroupOption } from '../GroupFormState/GroupForm/GroupDynamicTypeaheadSelect/UseGroupDynamicTypeaheadSelect/Types';

export interface Ready {
  kind: 'ready';
  group: Maybe<ExtendedGroupOption<string>>;
  participants: SuccessfulParticipant[];
  importedParticipantsResource: ImportedParticipantsResource;
}

export interface ReadyToSubmit {
  kind: 'ready-to-submit';
  group: ExtendedGroupOption<string>;
  participants: SuccessfulParticipant[];
  importedParticipantsResource: ImportedParticipantsResource;
}

export interface Submitting {
  kind: 'submitting';
  group: ExtendedGroupOption<string>;
  participants: SuccessfulParticipant[];
  importedParticipantsResource: ImportedParticipantsResource;
}

interface Success {
  kind: 'success';
  group: ExtendedGroupOption<string>;
  participants: SuccessfulParticipant[];
  importedParticipantsResource: ImportedParticipantsResource;
}

export const ready = (
  participants: SuccessfulParticipant[],
  importedParticipantsResource: ImportedParticipantsResource,
): Ready => {
  return {
    kind: 'ready',
    group: nothing(),
    participants,
    importedParticipantsResource,
  };
};

export const setGroup = (group: ExtendedGroupOption<string>, state: Ready): ReadyToSubmit => {
  const { kind, ...previous } = state;
  return {
    kind: 'ready-to-submit',
    ...previous,
    group,
  };
};

export const unsetGroup = (state: ReadyToSubmit): Ready => {
  const { kind, ...previous } = state;
  return {
    kind: 'ready',
    ...previous,
    group: nothing(),
  };
};

export const removeParticipant = (
  participant: SuccessfulParticipant,
  state: ReadyToSubmit | Ready,
): ReadyToSubmit | Ready => {
  const { kind, ...previous } = state;
  const participants = state.participants.filter((p) => p.email !== participant.email);
  switch (state.kind) {
    case 'ready':
      return {
        kind: 'ready',
        ...previous,
        group: nothing(),
        participants,
      };
    case 'ready-to-submit':
      return {
        kind: 'ready-to-submit',
        ...previous,
        group: state.group,
        participants,
      };
    default:
      assertNever(state);
  }
};

export const submit = (state: ReadyToSubmit): Submitting => {
  const { kind, ...previous } = state;
  return {
    kind: 'submitting',
    ...previous,
  };
};

export const success = (state: Submitting): Success => {
  const { kind, ...previous } = state;
  return {
    kind: 'success',
    ...previous,
  };
};

export type AddParticipantsToGroupFormState = Ready | ReadyToSubmit | Submitting | Success | Error;
export type AddParticipantsToGroupFormError = AppyError | InvalidUrlError | MissingLinkError;
